import { useEffect, useState } from 'react'
import { Head, useForm } from '@inertiajs/react'
import { Card } from '../../components/core/Card.jsx'
import { Button } from '../../components/core/Button.jsx'
import { Field } from '../../components/forms/Field.jsx'
import { Input } from '../../components/forms/Input.jsx'
import { Textarea } from '../../components/forms/Textarea.jsx'
import { Alert } from '../../components/feedback/Alert.jsx'
import { withSignedInLayout } from '../../signed_in/SignedInLayout.jsx'
import { quoted } from '~/marketing/marketingData'

export default function BrandsEdit({ brand = {}, errors = {} }) {
  // `?? ''` at the point of use rather than a default parameter: `brands.domain`
  // and `brands.aliases` are both nullable, Rails sends JSON null, and a default
  // parameter fires only for undefined — which is the uncontrolled-input defect
  // this repo has now hit three times.
  const form = useForm({ domain: brand.domain ?? '', aliases: brand.aliases ?? '' })
  const preview = useAliasPreview(brand.id, form.data.aliases, brand.aliases ?? '')
  const remove = useForm({ confirm: '' })
  const [confirming, setConfirming] = useState(false)
  const typedMatches = remove.data.confirm.trim() === (brand.name || '').trim()

  function save(e) {
    e.preventDefault()
    form.patch(`/brands/${brand.id}`)
  }

  function destroy(e) {
    e.preventDefault()
    remove.delete(`/brands/${brand.id}`)
  }

  return (
    <>
      <Head title={brand.name ? brand.name : 'Business'} />
      <main className="mx-auto min-h-screen w-full max-w-2xl px-6 py-12">
        <header className="mb-8 flex flex-col gap-2">
          <h1 className="page-title">{brand.name}</h1>
          <p className="text-[13.5px] text-[color:var(--text-muted)]">
            {brand.trade}
            {brand.city ? ` · ${brand.city}, ${brand.region}` : null}
          </p>
        </header>

        <div className="flex flex-col gap-6">
          <Card title="Website and names" description="What we match your answers against">
            <form onSubmit={save} className="flex flex-col gap-5">
              <Field
                label="Website"
                htmlFor="domain"
                hint="Used to tell whether your own pages are cited as sources. Changing it re-matches the answers already collected."
                error={errors.domain}
              >
                <Input
                  id="domain"
                  name="domain"
                  type="text"
                  inputMode="url"
                  iconLeft="globe"
                  placeholder="e.g. cumberlandcomfort.com"
                  value={form.data.domain}
                  invalid={Boolean(errors.domain)}
                  onChange={(e) => form.setData('domain', e.target.value)}
                />
              </Field>
              {/*
                The same field setup offers, on the screen where somebody who
                has read their answers discovers what the engines actually call
                them. `brands.aliases` had a writer at setup and nowhere to
                change one afterwards, so a business that learned this a week
                in had to delete and rebuild — losing every answer it had.

                A textarea rather than repeatable inputs, for the reason the
                setup form gives: the wire format stays a single scalar, and
                `params.permit(:aliases)` drops an array silently.
              */}
              <Field
                label="Other names for this business"
                htmlFor="aliases"
                hint="Optional, one per line. A trading name, a former name, or an abbreviation customers use. Spelling, punctuation and spacing are already handled."
                error={errors.aliases}
              >
                <Textarea
                  id="aliases"
                  name="aliases"
                  rows={3}
                  placeholder={'e.g. Cumberland Comfort\nCCS Heating & Air'}
                  value={form.data.aliases}
                  invalid={Boolean(errors.aliases)}
                  onChange={(e) => form.setData('aliases', e.target.value)}
                />
              </Field>

              <AliasPreview preview={preview} />

              <div>
                <Button variant="primary" as="button" loading={form.processing}>
                  Save changes
                </Button>
              </div>
            </form>
          </Card>

          <Card title="Industry, city and state" description="Read-only, with the reason">
            <div className="flex flex-col gap-5">
              <div className="grid gap-4 sm:grid-cols-3">
                <ReadOnly label="Industry" value={brand.trade} />
                <ReadOnly label="City" value={brand.city} />
                <ReadOnly label="State" value={brand.region} />
              </div>
              <p className="max-w-[72ch] text-[13.5px] text-[color:var(--text-body)]">
                Each of these decides what is collected and what every past number means, so a
                different industry or city is a second business rather than an edit. Plans are sized
                in businesses.
              </p>
              {/*
                **The badge here said "Not built yet" directly above a working
                remedy**, removed 2026-10-09 by the tone pass. Removing a
                business has shipped since 2026-09-20 and adding one is the
                screen next door, so the route this paragraph describes is
                built. What is not built is editing a city *in place* — and that
                is a decision rather than a backlog item, recorded in
                `PROJECT_STATE` as "a different city is a second business". A
                badge that files a decision as unfinished work tells a customer
                to stop reading before the answer, which is tone rule 8 failing
                in the direction that costs something.

                The last sentence read "Nothing measured for it can move across"
                — rule 1, absence as the subject, and it answers a question
                about the old business when the reader is asking about the new
                one.
              */}
              <div className="flex flex-col gap-2 rounded-[10px] border border-dashed border-[color:var(--border-strong)] p-4">
                <span className="text-[13.5px] font-medium">If the city here is wrong</span>
                <p className="max-w-[72ch] text-[13.5px] text-[color:var(--text-muted)]">
                  Adding a second city is a second business. Correcting a city chosen by mistake is
                  not: remove this business and add it again with the right one. The new one starts
                  from its first collection, because the old one was measuring a different market.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Remove this business" description="Collection stops; nothing else is kept back">
            <div className="flex flex-col gap-4">
              <p className="max-w-[72ch] text-[13.5px] text-[color:var(--text-body)]">
                Removing a business stops collecting for it and removes its questions and the answers
                collected for them. Your other businesses are untouched.
              </p>
              {!confirming ? (
                <div>
                  <Button variant="secondary" as="button" onClick={() => setConfirming(true)}>
                    Remove this business
                  </Button>
                </div>
              ) : (
                <form onSubmit={destroy} className="flex flex-col gap-4">
                  <Field
                    label={`Type ${brand.name} to confirm`}
                    htmlFor="confirm_name"
                    error={errors.confirm}
                  >
                    <Input
                      id="confirm_name"
                      name="name"
                      value={remove.data.confirm}
                      invalid={Boolean(errors.confirm)}
                      onChange={(e) => remove.setData('confirm', e.target.value)}
                    />
                  </Field>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="danger"
                      as="button"
                      disabled={!typedMatches}
                      loading={remove.processing}
                    >
                      Remove {brand.name}
                    </Button>
                    <Button variant="ghost" as="button" onClick={() => setConfirming(false)}>
                      Keep it
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </Card>
        </div>
      </main>
    </>
  )
}

// **The guard, and it is a preview rather than a rule.** An editable alias is a
// lever on the customer's own figure: `Brand#matches?` finds a target inside any
// longer name, so "Air" would claim every competitor with "air" in it, and the
// save recomputes every answer already collected. Three ways of blocking that
// were measured against 18,000 mentions and all three fail — a length floor
// rejects `a1air` and `spa35`, which are real businesses; exact matching loses
// ~1,450 genuine matches; word boundaries lose 253 more, since `normalize`
// removes whitespace. What makes "Air" bad is that it is a category word, and
// that is not in the string.
//
// So nothing is refused and the consequence is shown instead, in the customer's
// own answers, before they commit. It is the same comparison the save runs, with
// no writes — the preview cannot promise one thing and the recompute do another.
//
// Debounced at 350ms like the city check, and compared against the saved value
// so an untouched form asks nothing.
function useAliasPreview(brandId, typed, saved) {
  const [preview, setPreview] = useState(null)

  useEffect(() => {
    if (!brandId || typed.trim() === saved.trim()) {
      setPreview(null)
      return
    }
    let live = true
    const timer = setTimeout(() => {
      fetch(`/brands/${brandId}/alias_preview?aliases=${encodeURIComponent(typed)}`, {
        headers: { Accept: 'application/json' },
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        // A failed preview shows nothing rather than a zero. "This would match
        // no one" and "we could not check" are different facts, and the second
        // one wearing the first one's clothes is what would make a bad alias
        // look safe.
        .then((body) => live && setPreview(body))
        .catch(() => live && setPreview(null))
    }, 350)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [brandId, typed, saved])

  return preview
}

// Counts first, then the names, because the number is what a reader checks and
// the examples are what make a wrong name obvious. `data` rather than a warning
// tone: widening the names is usually correct, and this is a receipt rather
// than an alarm — the one case it should feel like a warning announces itself
// in the examples without the colour saying so.
function AliasPreview({ preview }) {
  if (!preview) return null
  const {
    newly_matched: gained = 0,
    gained_sample: gainedNames = [],
    no_longer_matched: lost = 0,
    lost_sample: lostNames = [],
  } = preview
  if (!gained && !lost) return null

  return (
    <Alert tone="data" icon="activity" title="What this changes in answers already collected">
      <div className="flex flex-col gap-2">
        {/*
          All three sentences are read from `quoted`, never written here — they
          are Class 2, and a page holding one is what
          `sanctioned_copy_ownership_test` exists to refuse.

          The removal line was the exception until 2026-10-10: a literal here,
          unsanctioned, recorded as outstanding in `copy-rules.md` rather than
          left for somebody to find. It reads from its source now, and the
          sentence it replaced is held out by `withdrawn_copy_test`.
        */}
        {gained > 0 && <p>{quoted.alias_claims(gained, gainedNames.join(', '))}</p>}
        {lost > 0 && <p>{quoted.alias_releases(lost, lostNames.join(', '))}</p>}
        <p className="text-[color:var(--text-muted)]">{quoted.alias_recompute}</p>
      </div>
    </Alert>
  )
}

function ReadOnly({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-[0.09em] text-[color:var(--text-muted)]">
        {label}
      </span>
      <span className="text-[15px]">{value || '—'}</span>
    </div>
  )
}

BrandsEdit.layout = withSignedInLayout
